import axios from 'axios';
import store from '../store'; // Changed to default import
import { refreshAccessToken } from '../store/slices/authSlice'; // Ensure this is correctly exported from authSlice

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL, // API 기본 URL 환경 변수 사용
});

// 요청 인터셉터: 모든 요청에 액세스 토큰 추가 및 withCredentials 설정
api.interceptors.request.use(
  (config) => {
    const token = store.getState().auth.token;
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // API 요청 시 withCredentials를 true로 설정하여 쿠키를 주고받을 수 있도록 함
    config.withCredentials = true; 
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터: 401 에러 발생 시 토큰 갱신 시도
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 401 에러이고, 재시도 요청이 아니며, refresh token 요청 자체가 실패한게 아닐 때
    if (error.response?.status === 401 && !originalRequest._retry && originalRequest.url !== '/api/auth/refresh-token') {
      if (isRefreshing) {
        // 이미 토큰 갱신 중이면, 요청을 큐에 넣고 대기
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err); // 큐에 있던 요청들도 실패 처리
        });
      }

      originalRequest._retry = true; // 재시도 플래그 설정
      isRefreshing = true;

      try {
        console.log('🔄 Attempting to refresh token via interceptor...');
        // dispatch 후 unwrap() 대신 resultAction을 직접 사용해 fulfilled/rejected를 확인
        const resultAction = await store.dispatch(refreshAccessToken());
        
        if (refreshAccessToken.fulfilled.match(resultAction)) {
          const newAccessToken = resultAction.payload.access_token;
          console.log('✅ Token refreshed by interceptor. New Access Token:', newAccessToken);
          processQueue(null, newAccessToken); // 대기 중인 요청들 성공 처리
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          return api(originalRequest); // 원래 요청 재시도
        } else {
          // refreshAccessToken.rejected.match(resultAction) 또는 다른 실패 케이스
          const errorMessage = resultAction.payload?.detail || resultAction.payload || resultAction.error?.message || 'Token refresh failed';
          console.error('🚫 Token refresh failed via interceptor:', errorMessage);
          processQueue(new Error(errorMessage), null); // 대기 중인 요청들 실패 처리
          // logoutUser는 refreshAccessToken thunk 내부에서 실패 시 호출되도록 설계됨
          return Promise.reject(new Error(errorMessage));
        }
      } catch (e) { 
        // 이 catch 블록은 dispatch 자체가 실패하는 매우 예외적인 경우에만 해당될 수 있습니다.
        // thunk 내부의 로직 에러는 rejected 케이스로 처리됩니다.
        console.error('🚨 Critical error during token refresh dispatch:', e);
        processQueue(e, null);
        // logoutUser를 여기서 호출할 수도 있으나, thunk 내부 처리에 의존하는 것이 좋습니다.
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default api; 